import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Avatar,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Chip,
  Tab,
  Tabs,
  Breadcrumbs,
  Link as MuiLink,
  Button,
  Card,
  CardContent,
  Rating,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as BackIcon,
  OpenInNew as OpenInNewIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  LocalBar as BarIcon,
  Attractions as AttractionIcon,
  ShoppingBag as ShoppingIcon,
  Star as StarIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import {
  contentApi,
  type LocationCategory,
  type LocationItem,
} from '../../services/api-client';
import ContentStatusChip from '../../components/content/ContentStatusChip';

// =============================================================================
// Constants
// =============================================================================

const CATEGORIES: { value: LocationCategory; label: string; icon: React.ReactElement }[] = [
  { value: 'hotels', label: 'Hotels', icon: <HotelIcon /> },
  { value: 'restaurants', label: 'Restaurants', icon: <RestaurantIcon /> },
  { value: 'bars', label: 'Bars', icon: <BarIcon /> },
  { value: 'attractions', label: 'Attractions', icon: <AttractionIcon /> },
  { value: 'shopping', label: 'Shopping', icon: <ShoppingIcon /> },
];

// =============================================================================
// LocationList — locations for a city, with category tabs
// =============================================================================

export default function LocationList() {
  const { slug: citySlug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [category, setCategory] = useState<LocationCategory>('hotels');
  const [search, setSearch] = useState('');

  // Fetch locations for current category
  const {
    data: locationData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['content', 'locations', citySlug, category],
    queryFn: () => contentApi.getCityLocations(citySlug!, category),
    enabled: !!citySlug,
    staleTime: 2 * 60 * 1000,
  });

  const locations = locationData?.locations || [];
  const cityName = locationData?.cityName || citySlug;

  // Filter by search
  const filtered = search.trim()
    ? locations.filter(
        (l) =>
          l.name.toLowerCase().includes(search.toLowerCase()) ||
          l.description?.toLowerCase().includes(search.toLowerCase()) ||
          l.address?.city?.toLowerCase().includes(search.toLowerCase())
      )
    : locations;

  return (
    <Box>
      {/* Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Back to city">
            <IconButton size="small" onClick={() => navigate(`/content/cms/cities/${citySlug}`)}>
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Breadcrumbs separator="›" sx={{ fontSize: '0.875rem' }}>
            <MuiLink
              component="button"
              variant="body2"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate('/content/cms/cities')}
            >
              Cities
            </MuiLink>
            <MuiLink
              component="button"
              variant="body2"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate(`/content/cms/cities/${citySlug}`)}
            >
              {cityName}
            </MuiLink>
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
              Locations
            </Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {cityName} — Locations
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Manage hotels, restaurants, bars, attractions, and shopping venues
        </Typography>
      </Box>

      {/* Category tabs */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={CATEGORIES.findIndex((c) => c.value === category)}
          onChange={(_, idx: number) => {
            setCategory(CATEGORIES[idx].value);
            setSearch('');
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {CATEGORIES.map((cat) => (
            <Tab key={cat.value} icon={cat.icon} iconPosition="start" label={cat.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={`Search ${category}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {isLoading ? '...' : `${filtered.length} ${category}`}
        </Typography>
      </Paper>

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {!!error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load {category}. The Content API may not have data for this category.
        </Alert>
      )}

      {/* Location cards */}
      {!isLoading && !error && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filtered.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {search
                  ? `No ${category} match your search.`
                  : `No ${category} found for ${cityName}.`}
              </Typography>
            </Paper>
          )}

          {filtered.map((loc) => (
            <Card key={loc.slug} variant="outlined">
              <CardContent
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                  '&:last-child': { pb: 2 },
                }}
              >
                {/* Image */}
                <Avatar
                  variant="rounded"
                  src={loc.heroImage}
                  alt={loc.name}
                  sx={{ width: 100, height: 72, flexShrink: 0 }}
                >
                  <LocationIcon />
                </Avatar>

                {/* Details */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {loc.name}
                    </Typography>
                    {loc.priceRange && (
                      <Chip label={loc.priceRange} size="small" variant="outlined" />
                    )}
                    <ContentStatusChip status={loc.status || 'published'} />
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      mb: 1,
                    }}
                  >
                    {loc.description}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Rating */}
                    {loc.rating && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {loc.rating}
                        </Typography>
                        {loc.reviewCount && (
                          <Typography variant="caption" color="text.secondary">
                            ({loc.reviewCount.toLocaleString()} reviews)
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Address */}
                    {loc.address?.line1 && (
                      <Typography variant="caption" color="text.secondary">
                        {loc.address.line1}, {loc.address.city}
                      </Typography>
                    )}

                    {/* Cuisine */}
                    {loc.cuisine && (
                      <Chip label={loc.cuisine} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                    )}
                  </Box>

                  {/* Facilities */}
                  {loc.facilities && loc.facilities.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                      {loc.facilities.slice(0, 5).map((f) => (
                        <Chip
                          key={f}
                          label={f}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      ))}
                      {loc.facilities.length > 5 && (
                        <Chip
                          label={`+${loc.facilities.length - 5}`}
                          size="small"
                          sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                      )}
                    </Box>
                  )}
                </Box>

                {/* View link */}
                <Tooltip title="View on site">
                  <IconButton
                    size="small"
                    component="a"
                    href={`https://curatedby.tripsby.ai/cities/${citySlug}/${category}/${loc.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}
